/**
 * Typed client for the portal's own `/api/*` Worker routes.
 *
 * There is no cross-origin API client here and no token handling. The session
 * is an HttpOnly cookie the browser attaches on its own — JavaScript in this
 * bundle cannot read it, which is the point — and the Worker reaches
 * aloftwx-api over a service binding. The SPA talks only to its own origin.
 *
 * Nothing here sends an email address as an identity. Every scoped call takes
 * it from the session the Worker resolved; an address the browser could put in
 * a body would be a claim, not an identity.
 */

import type {
  ClaimOutcome,
  CustomerLicenseDetail,
  CustomerLicenseList,
  CustomerPingResult,
  CustomerRevealedKey,
  LinkRequested,
  SeatReleaseResult,
  SessionRecord,
  SessionState,
} from "../../../shared/portal";
import type { TicketDetail, TicketSummary } from "../../../shared/support";

const BASE = "/api";

/**
 * The `code` from the Worker's error envelope, carried so a route can branch.
 *
 * The console's equivalent surfaces `status` and `code` in a toast and moves
 * on, because two admins can read a status code. Here the `message` is written
 * for a customer and is usually the entire UI for the failure — the code is for
 * the handful of cases the screen genuinely handles differently, like telling
 * an expired sign-in link from a used one.
 */
export class PortalError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "PortalError";
  }
}

type ErrorBody = { error?: { code?: string; message?: string } };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "same-origin",
    ...init,
    headers: { accept: "application/json", ...(init.headers ?? {}) },
  });

  if (!res.ok) {
    let body: ErrorBody = {};
    try {
      body = (await res.json()) as ErrorBody;
    } catch {
      /* Non-JSON error page — a Cloudflare 5xx, or the Worker failing before
         it could render an envelope. Fall through to the generic message. */
    }
    throw new PortalError(
      res.status,
      body.error?.code ?? "http_error",
      body.error?.message ?? "Something went wrong at our end. Please try again in a moment.",
    );
  }

  // 204 has no body. Nothing returns one today, but a route that starts to
  // would otherwise fail here rather than at the call site.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function withSignal(signal: AbortSignal | undefined, init: RequestInit = {}): RequestInit {
  return signal ? { ...init, signal } : init;
}

const send = (method: string, body?: unknown): RequestInit => ({
  method,
  headers: body === undefined ? {} : { "content-type": "application/json" },
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

export const portalApi = {
  // ── session ────────────────────────────────────────────────────────────────

  /** Never rejects for "signed out" — that is a 200 with `signed_in: false`. */
  session(signal?: AbortSignal): Promise<SessionState> {
    return request<SessionState>("/session", withSignal(signal));
  },

  /**
   * Ask for a sign-in link.
   *
   * Resolves the same way for an address we know and one we do not, because
   * the Worker genuinely does the same thing for both. The only rejections are
   * a malformed address and a rate limit.
   */
  requestLink(email: string, next: string | null): Promise<LinkRequested> {
    return request<LinkRequested>("/auth/link", send("POST", { email, next }));
  },

  /**
   * Spend a sign-in link. `next` is the path the link was issued for, when it
   * carried one.
   *
   * Rejects with `link_expired`, `link_used`, `link_invalid` or
   * `account_blocked` — four different sentences, and the verify screen shows
   * each of them differently because they lead to different actions.
   */
  verifyLink(token: string): Promise<SessionState & { next?: string | null }> {
    return request<SessionState & { next?: string | null }>(
      "/auth/verify",
      send("POST", { token }),
    );
  },

  signOut(): Promise<SessionState> {
    return request<SessionState>("/auth/signout", send("POST"));
  },

  // ── licenses ───────────────────────────────────────────────────────────────

  /** Liveness of the licensing plane. Reachable signed out, on purpose. */
  ping(signal?: AbortSignal): Promise<CustomerPingResult> {
    return request<CustomerPingResult>("/ping", withSignal(signal));
  },

  listLicenses(signal?: AbortSignal): Promise<CustomerLicenseList> {
    return request<CustomerLicenseList>("/licenses", withSignal(signal));
  },

  getLicense(licenseId: string, signal?: AbortSignal): Promise<CustomerLicenseDetail> {
    return request<CustomerLicenseDetail>(
      `/licenses/${encodeURIComponent(licenseId)}`,
      withSignal(signal),
    );
  },

  /**
   * Read the license key back.
   *
   * Deliberately not `useApiQuery`-shaped: this is called from a button, never
   * on mount. Every open writes an audit row in aloftwx-api, so a panel that
   * revealed on render would log a credential read for every idle page load —
   * and put a live key into every screenshot a customer attaches to a support
   * email. The asking is the record.
   *
   * Rejects 409 for a license issued before keys were stored, which the panel
   * renders as a support prompt rather than a retry: there is no self-serve
   * fix, because rotation is an operator action.
   */
  revealKey(licenseId: string): Promise<Extract<CustomerRevealedKey, { available: true }>> {
    return request<Extract<CustomerRevealedKey, { available: true }>>(
      `/licenses/${encodeURIComponent(licenseId)}/key`,
    );
  },

  releaseSeat(licenseId: string, activationId: string): Promise<SeatReleaseResult> {
    return request<SeatReleaseResult>(
      `/licenses/${encodeURIComponent(licenseId)}/seats/${encodeURIComponent(activationId)}/release`,
      send("POST"),
    );
  },

  /**
   * Bind a key to this account.
   *
   * Resolves for every outcome the contract defines including the misses —
   * `not_found` and `claimed_by_other` are answers, not errors, and each one is
   * a different sentence on screen. It rejects only for a malformed key (400)
   * and the throttle (429).
   */
  claimLicense(key: string): Promise<ClaimOutcome> {
    return request<ClaimOutcome>("/licenses/claim", send("POST", { key }));
  },

  // ── account ────────────────────────────────────────────────────────────────

  listDevices(signal?: AbortSignal): Promise<{ devices: SessionRecord[] }> {
    return request<{ devices: SessionRecord[] }>("/session/devices", withSignal(signal));
  },

  revokeDevice(sessionId: string): Promise<{ revoked: number }> {
    return request<{ revoked: number }>(
      `/session/devices/${encodeURIComponent(sessionId)}/revoke`,
      send("POST"),
    );
  },

  revokeOtherDevices(): Promise<{ revoked: number }> {
    return request<{ revoked: number }>("/session/devices/revoke-others", send("POST"));
  },

  setDisplayName(displayName: string | null): Promise<{ display_name: string | null }> {
    return request<{ display_name: string | null }>(
      "/account",
      send("PATCH", { display_name: displayName }),
    );
  },

  // ── support ────────────────────────────────────────────────────────────────

  listTickets(signal?: AbortSignal): Promise<{ tickets: TicketSummary[] }> {
    return request<{ tickets: TicketSummary[] }>("/support/tickets", withSignal(signal));
  },

  getTicket(ticketId: string, signal?: AbortSignal): Promise<TicketDetail> {
    return request<TicketDetail>(
      `/support/tickets/${encodeURIComponent(ticketId)}`,
      withSignal(signal),
    );
  },

  /**
   * Open a ticket.
   *
   * `license_id` / `license_key_prefix` are context, not authorization —
   * nothing server-side reads a license from them. They exist so an operator
   * opens the ticket already knowing which key it is about, which is the
   * difference between a support thread and a contact form.
   */
  createTicket(input: {
    subject: string;
    message: string;
    category: string;
    license_id?: string | null;
    license_key_prefix?: string | null;
    attachments?: OutgoingAttachment[];
  }): Promise<{ id: string; number: number }> {
    return request<{ id: string; number: number }>("/support/tickets", send("POST", input));
  },

  replyToTicket(
    ticketId: string,
    message: string,
    attachments: OutgoingAttachment[] = [],
  ): Promise<{ ok: true; reopened: boolean }> {
    return request<{ ok: true; reopened: boolean }>(
      `/support/tickets/${encodeURIComponent(ticketId)}/messages`,
      send("POST", { message, attachments }),
    );
  },

  resolveTicket(ticketId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/support/tickets/${encodeURIComponent(ticketId)}/resolve`,
      send("POST"),
    );
  },

  /**
   * The URL an attachment is rendered from.
   *
   * A plain same-origin path, not a signed bucket URL: the Worker re-checks
   * that the ticket belongs to the session on every read, so an <img src> is
   * authenticated by the cookie the browser already holds. The bucket itself
   * has no public URL at all.
   */
  attachmentUrl(ticketId: string, attachmentId: string): string {
    return `${BASE}/support/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`;
  },
};

/** What the client sends up. `data_base64` may carry a `data:` prefix. */
export type OutgoingAttachment = {
  filename: string;
  data_base64: string;
};
